import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import { faqs } from '@/constants/content'

interface FaqProps {
    items?: Array<{ question: string, answer: string }>
    heading?: string
}

interface FaqItemProps {
    question: string
    answer: string
    index: number
    open: boolean
    onToggle: () => void
}

const FaqItem: FC<FaqItemProps> = ({ question, answer, index, open, onToggle }): ReactNode => {
    const buttonId = `faq-question-${index}`
    const panelId = `faq-answer-${index}`

    return (
        <div className='border-b-2 border-ink'>
            <h3>
                <button
                    id={buttonId}
                    type='button'
                    aria-expanded={open}
                    aria-controls={panelId}
                    onClick={onToggle}
                    className='group flex w-full items-center justify-between gap-6 py-6 text-left sm:py-7'
                >
                    <span className='font-heading text-xl font-bold leading-snug tracking-[-0.02em] transition-colors sm:text-2xl lg:text-[1.75rem]'>
                        {question}
                    </span>

                    <span
                        aria-hidden='true'
                        className={cn(
                            'grid size-11 shrink-0 place-items-center rounded-full border-2 border-ink transition-[background-color,rotate] duration-500 ease-bounce',
                            open ? 'rotate-[135deg] bg-[#21CECD]' : 'bg-paper group-hover:bg-[#21CECD]/35'
                        )}
                    >
                        <Plus className='size-5' />
                    </span>
                </button>
            </h3>

            <div
                id={panelId}
                role='region'
                aria-labelledby={buttonId}
                className='grid transition-[grid-template-rows] duration-500 ease-snap'
                style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
            >
                <div className='min-h-0 overflow-hidden' inert={!open}>
                    <p className='max-w-[44rem] pb-7 text-lg leading-relaxed text-ink/75 sm:text-xl'>
                        {answer}
                    </p>
                </div>
            </div>
        </div>
    )
}

// Defaults to the homepage questions. Other pages pass their own list and heading.
const Faq: FC<FaqProps> = ({ items = faqs, heading = 'Frequently asked questions' }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reduce = useReduceMotion()
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] })
    const rotate = useTransform(scrollYProgress, [0, 1], [-24, 18])

    return (
        <section ref={sectionRef} id='faq' aria-labelledby='faq-heading' className='relative overflow-x-clip border-t-2 border-ink bg-paper py-20 sm:py-28 lg:py-36'>
            <h2 id='faq-heading' className='sr-only'>
                {heading}
            </h2>

            <div className='shell grid gap-6 lg:grid-cols-12 lg:gap-10'>
                <div aria-hidden='true' className='lg:col-span-4'>
                    <div className='lg:sticky lg:top-28'>
                        <motion.span
                            style={reduce ? undefined : { rotate }}
                            className='text-outline ml-6 block w-fit origin-bottom sm:ml-8 lg:ml-10 font-heading text-[9rem] font-extrabold leading-[0.8] wdth-75 [--outline-width:3px] sm:text-[12rem] lg:text-[18rem]'
                        >
                            ?
                        </motion.span>
                    </div>
                </div>

                <div className='border-t-2 border-ink lg:col-span-8'>
                    {items.map((faq, index) => (
                        <FaqItem
                            key={faq.question}
                            question={faq.question}
                            answer={faq.answer}
                            index={index}
                            open={openIndex === index}
                            onToggle={() => setOpenIndex(current => current === index ? null : index)}
                        />
                    ))}
                </div>
            </div>
        </section>
    )
}

export default Faq
